import { useMemo } from "react";

const VARIANT_COLORS: Record<string, string> = {
  success: "#00633F", // forest (accessible, design-system)
  warn: "#b45309", // amber-700 (darker orange for contrast on light yellow)
  danger: "#EF4444", // red-500 (matches control status cells)
  default: "#6b7280", // gray-500
};

export interface RadialProgressProps {
  progress: number;
  radius?: number;
  strokeWidth?: number;
  variant?: "success" | "warn" | "danger";
  className?: string;
}

export const RadialProgress = ({
  progress,
  radius = 32,
  strokeWidth = 4,
  variant = "warn",
  className = "",
}: RadialProgressProps) => {
  const { normalizedRadius, circumference, strokeDashoffset, strokeColor } =
    useMemo(() => {
      const normalizedRadius = Math.max(radius - strokeWidth * 2, 0);
      const circumference = normalizedRadius * 2 * Math.PI;
      const clampedProgress = Math.max(0, Math.min(progress, 100));
      const strokeDashoffset =
        circumference - (clampedProgress / 100) * circumference;
      const strokeColor = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;

      return { normalizedRadius, circumference, strokeDashoffset, strokeColor };
    }, [progress, radius, strokeWidth, variant]);

  const size = radius * 2;

  return (
    <svg
      className={className}
      height={size}
      width={size}
      role="presentation"
      viewBox={`0 0 ${size} ${size}`}
    >
      <circle
        stroke={strokeColor}
        fill="transparent"
        strokeWidth={strokeWidth}
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={strokeDashoffset}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
        style={{
          transition: "stroke-dashoffset 0.35s ease",
          transform: "rotate(-90deg)",
          transformOrigin: "50% 50%",
        }}
      />
    </svg>
  );
};
